import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { getHomePath, isListingOnlyUser, isPathAllowedForListingOnly, LISTING_TEAM_PATH } from '@/lib/accountType';
import type { ReactElement } from 'react';
import { lazy, Suspense, useEffect } from 'react';
import { readCandidateSession } from '@/careers/lib/session';
import { isCandidateAreaOrigin, isJobsSiteOrigin } from '@/lib/jobsSite';
import {
  CAREERS_ROUTES,
  JOBS_SITE_COMPANY_ROUTE,
  JOBS_SITE_JOB_ROUTE,
  PUBLIC_COMPANY_ROUTE,
  PUBLIC_JOB_ROUTE,
} from '@/careers/constants';
import { PUBLIC_JOB_LISTINGS_PATH } from '@/constants/jobListing';
import { useAuth } from './hooks/useAuth';
import { MODULE_KEYS, Permission, RoleType } from '@hr-outsourcing/shared';
import { ModuleGate } from '@/components/ModuleGate';
import LoginPage from './pages/LoginPage';
import { SIGNUP_PATH } from '@/constants/signup';
import { SettingsRoute } from '@/components/Settings/SettingsRoute';
import { CANDIDATE_OAUTH_RESULT_PATH, LOGIN_PATH } from '@/constants/login';
import LandingPage from './pages/LandingPage';
import { useNotificationInitializer } from '@/hooks/UseNotificationInitializer.tsx';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { PublicFooter } from '@/components/PublicFooter';
import { MODULE_PAGE_BASE_PATH } from '@/constants/modulePages';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

/** Yeni pathname'e yönlendirirken query string'i (ör. /reports?startDate=...) korur. */
const RedirectPreservingQuery = ({ to }: { to: string }) => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
};

const Layout = lazy(() => import('./components/Layout'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const AnnouncementsPage = lazy(() => import('./pages/AnnouncementsPage'));
const CandidatesPage = lazy(() => import('./pages/CandidatesPage'));
const JobApplicationsPage = lazy(() => import('./pages/JobApplicationsPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const JobPositionsPage = lazy(() => import('./pages/JobPositionsPage'));
const PositionDetailPage = lazy(() => import('./pages/PositionDetailPage'));
const InterviewsPage = lazy(() => import('./pages/InterviewsPage'));
const ExamsPage = lazy(() => import('./pages/ExamsPage'));
const LeavesPage = lazy(() => import('./pages/LeavesPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const PositionReportsPage = lazy(() => import('./pages/PositionReportsPage'));
const CustomerCompanyPage = lazy(() => import('./pages/CustomerCompanyPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const SkillsAndTitlesPage = lazy(() => import('@/pages/SkillsAndTitlesPage'));
const CandidateMatchingPage = lazy(() => import('@/pages/CandidateMatchingPage'));
const RecruitmentPipelinePage = lazy(() => import('@/pages/RecruitmentPipelinePage'));
const CompaniesPage = lazy(() => import('@/pages/CompaniesPage'));
const SystemPage = lazy(() => import('@/pages/SystemPage'));
const InvoicesPage = lazy(() => import('@/pages/InvoicesPage'));
const InvoiceDetailPage = lazy(() => import('@/pages/InvoiceDetailPage'));
const CompanyInvitePage = lazy(() => import('@/pages/CompanyInvitePage'));
const CompanyDashboardPage = lazy(() => import('@/pages/CompanyDashboardPage'));
const CompanyJobPositionsPage = lazy(() => import('@/pages/CompanyJobPositionsPage'));
const CompanyJobPositionDetailPage = lazy(() => import('@/pages/CompanyJobPositionDetailPage'));
const CompanyConnectPage = lazy(() => import('@/pages/CompanyConnectPage'));
const CompanyLayout = lazy(() => import('@/components/CompanyLayout'));
const JobPostingPage = lazy(() => import('@/pages/JobPostingPage'));
const AccountSettingsPage = lazy(() => import('./pages/AccountSettingsPage'));
const ListingTeamPage = lazy(() => import('./pages/ListingTeamPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const GroupsPage = lazy(() => import('./pages/GroupsPage'));
const GroupDetailPage = lazy(() => import('./pages/GroupDetailPage'));
const CLevelHierarchyPage = lazy(() => import('./pages/CLevelHierarchyPage'));
const Performance360FormsPage = lazy(() => import('./pages/Performance360FormsPage'));
const Performance360EvaluationsPage = lazy(() => import('./pages/Performance360EvaluationsPage'));
const Performance360GoalsPage = lazy(() => import('./pages/Performance360GoalsPage'));
const Performance360FormCreatePage = lazy(() => import('./pages/Performance360FormCreatePage'));
const Performance360MyResultsPage = lazy(() => import('./pages/Performance360MyResultsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const SudokuGamePage = lazy(() => import('./pages/games/SudokuGamePage'));
const EmployeesPage = lazy(() => import('@/pages/EmployeesPage').then((m) => ({ default: m.EmployeesPage })));
const EmployeeDetailPage = lazy(() => import('@/pages/EmployeeDetailPage'));
const OffboardingProcessPage = lazy(() => import('@/pages/OffboardingProcessPage'));
const OffboardingListPage = lazy(() => import('@/pages/OffboardingListPage'));
const MyOffboardingPage = lazy(() => import('@/pages/MyOffboardingPage'));
const InventoryPage = lazy(() => import('@/pages/InventoryPage'));
const CandidateProfilePage = lazy(() =>
  import('@/pages/CandidateProfilePage').then((m) => ({ default: m.CandidateProfilePage }))
);
const ExpensesManagementPage = lazy(() =>
  import('@/pages/ExpensesManagementPage').then((m) => ({ default: m.ExpensesManagementPage }))
);
const OvertimePage = lazy(() => import('@/pages/OvertimePage').then((m) => ({ default: m.OvertimePage })));
const KvkkApprovalPage = lazy(() => import('./pages/KvkkApprovalPage').then((m) => ({ default: m.KvkkApprovalPage })));
const OAuthAuthorizePage = lazy(() => import('./pages/OAuthAuthorizePage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const TermsAndConditionsPage = lazy(() => import('./pages/TermsAndConditionsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const RefundPage = lazy(() => import('./pages/RefundPage'));
const LeaveVerificationPage = lazy(() => import('./pages/LeaveVerificationPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
const CandidateOAuthPage = lazy(() => import('./pages/CandidateOAuthPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const CareersAboutPage = lazy(() =>
  import('@/careers/pages/CareersAboutPage').then((m) => ({ default: m.CareersAboutPage }))
);
const ModulePageRoute = lazy(() => import('./pages/modules/ModulePageRoute'));
const DistanceSalesPage = lazy(() => import('./pages/DistanceSalesPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const PackageManagementPage = lazy(() => import('./pages/PackageManagementPage'));
const ChatDialog = lazy(() => import('@/components/ChatBot').then((m) => ({ default: m.ChatDialog })));
const CareersLayout = lazy(() => import('@/careers/components/CareersLayout').then((m) => ({ default: m.CareersLayout })));
const CareersDashboardPage = lazy(() => import('@/careers/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const CareersCvPage = lazy(() => import('@/careers/pages/CvPage').then((m) => ({ default: m.CvPage })));
const CareersProfilePage = lazy(() => import('@/careers/pages/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const CareersRecommendedPage = lazy(() => import('@/careers/pages/RecommendedPage').then((m) => ({ default: m.RecommendedPage })));
const CareersSavedJobsPage = lazy(() => import('@/careers/pages/SavedJobsPage').then((m) => ({ default: m.SavedJobsPage })));
const CareersApplicationsPage = lazy(() => import('@/careers/pages/ApplicationsPage').then((m) => ({ default: m.ApplicationsPage })));
const CareersInterviewsPage = lazy(() => import('@/careers/pages/InterviewsPage').then((m) => ({ default: m.InterviewsPage })));
const CareersJobDetailPage = lazy(() => import('@/careers/pages/JobDetailPage').then((m) => ({ default: m.JobDetailPage })));
const PublicJobsPage = lazy(() => import('@/careers/pages/PublicJobsPage').then((m) => ({ default: m.PublicJobsPage })));
const PublicJobPage = lazy(() => import('@/careers/pages/PublicJobPage').then((m) => ({ default: m.PublicJobPage })));
const PublicCompanyPage = lazy(() =>
  import('@/careers/pages/CompanyPage').then((m) => ({ default: m.PublicCompanyPage }))
);
const CareersCompanyPage = lazy(() => import('@/careers/pages/CompanyPage').then((m) => ({ default: m.CompanyPage })));

const PrivateRoutes = () => {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) {
    return <Navigate to='/' replace />;
  }
  // A listing-only company only has positions, titles/skills, notifications and account
  // settings; every other page (dashboard included) sends it back to its home page.
  if (isListingOnlyUser(user) && !isPathAllowedForListingOnly(location.pathname)) {
    return <Navigate to={getHomePath(user)} replace />;
  }
  return (
    <Suspense fallback={null}>
      <Layout>
        <Outlet />
      </Layout>
    </Suspense>
  );
};
const PublicRoutes = () => {
  const { user } = useAuth();
  const location = useLocation();
  const isResetPasswordPage = location.pathname.startsWith('/reset-password');
  const isRegisterPage = location.pathname.startsWith('/register');
  const isKvkkApprovalPage =
    location.pathname.startsWith('/kvkk-approval') || location.pathname.startsWith('/kvkk-onay');
  if (!isResetPasswordPage && !isRegisterPage && !isKvkkApprovalPage) {
    if (user) return <Navigate to={getHomePath(user)} replace />;
    // A candidate already signed in (in this tab or another) carries on as that candidate.
    if (readCandidateSession()) return <Navigate to={CAREERS_ROUTES.dashboard} replace />;
  }
  return <Outlet />;
};
const CareersRoutes = () => {
  // With the listings on their own domain the candidate area lives only there; on Peoply's site the
  // server moves /careers there (301) and the app does not draw it either.
  if (!isCandidateAreaOrigin()) return <Navigate to='/' replace />;
  const session = readCandidateSession();
  if (!session) {
    return <Navigate to={LOGIN_PATH} replace />;
  }
  return (
    <Suspense fallback={null}>
      <CareersLayout>
        <Outlet />
      </CareersLayout>
    </Suspense>
  );
};

const CompanyRoutes = () => {
  const { user } = useAuth();
  if (!user || !user.isCompany) {
    return <Navigate to='/' replace />;
  }
  return (
    <Suspense fallback={null}>
      <CompanyLayout>
        <Outlet />
      </CompanyLayout>
    </Suspense>
  );
};

// The candidate area's pages, the same on Peoply's site and on the listings' own domain.
const careersAreaRoutes = (
  <>
    <Route path='/careers/dashboard' element={<CareersDashboardPage />} />
    <Route path='/careers/cv' element={<CareersCvPage />} />
    <Route path='/careers/profile' element={<CareersProfilePage />} />
    <Route path='/careers/recommended' element={<CareersRecommendedPage />} />
    <Route path='/careers/saved' element={<CareersSavedJobsPage />} />
    <Route path='/careers/applications' element={<CareersApplicationsPage />} />
    <Route path='/careers/interviews' element={<CareersInterviewsPage />} />
    <Route path='/careers/jobs/:slug' element={<CareersJobDetailPage />} />
    <Route path='/careers/companies/:slug' element={<CareersCompanyPage />} />
    <Route path='/careers/*' element={<Navigate to={CAREERS_ROUTES.dashboard} replace />} />
  </>
);

// Peoply Kariyer's own domain (JOBS_SITE_URL): only the candidate app's pages. Peoply's own pages
// (the HR app, pricing, module pages…) have no address there — the server answers them 404 — and a
// single-segment address is a listing's.
const JobsSiteRoutes = () => (
  <Suspense fallback={null}>
    <Routes>
      <Route path='/' element={<PublicJobsPage />} />
      <Route path={JOBS_SITE_COMPANY_ROUTE} element={<PublicCompanyPage />} />
      <Route path='/about' element={<CareersAboutPage />} />
      <Route path='/privacy' element={<PrivacyPage />} />
      <Route path='/terms-and-conditions' element={<TermsAndConditionsPage />} />
      <Route path={CANDIDATE_OAUTH_RESULT_PATH} element={<CandidateOAuthPage />} />
      <Route element={<PublicRoutes />}>
        <Route path={LOGIN_PATH} element={<LoginPage />} />
        <Route path={SIGNUP_PATH} element={<SignupPage />} />
        <Route path='/reset-password' element={<ResetPasswordPage />} />
      </Route>
      <Route element={<CareersRoutes />}>{careersAreaRoutes}</Route>
      <Route path={JOBS_SITE_JOB_ROUTE} element={<PublicJobPage />} />
      <Route path='*' element={<NotFoundPage />} />
    </Routes>
  </Suspense>
);

const getAuthorizedHomePath = (user: ReturnType<typeof useAuth>['user']) => getHomePath(user);

const AuthorizedRoute = ({
  children,
  permissions,
  roles,
  superAdminOnly = false,
  predicate,
}: {
  children: ReactElement;
  permissions?: Permission[];
  roles?: RoleType[];
  superAdminOnly?: boolean;
  predicate?: (auth: ReturnType<typeof useAuth>) => boolean;
}) => {
  const auth = useAuth();
  const { user, hasAnyPermission, ability } = auth;

  if (!user) {
    return <Navigate to='/' replace />;
  }

  if (superAdminOnly) {
    return user.isSuperAdmin ? children : <Navigate to={getAuthorizedHomePath(user)} replace />;
  }

  if (user.isSuperAdmin) {
    return <Navigate to={getAuthorizedHomePath(user)} replace />;
  }

  if (roles && roles.length > 0) {
    const roleName = user.role?.name;
    const hasAllowedRole = roles.some((role) => roleName === role);
    if (!hasAllowedRole) {
      return <Navigate to={getAuthorizedHomePath(user)} replace />;
    }
  }

  if (permissions && permissions.length > 0 && !hasAnyPermission(permissions)) {
    return <Navigate to={getAuthorizedHomePath(user)} replace />;
  }

  if (predicate && !predicate({ ...auth, ability })) {
    return <Navigate to={getAuthorizedHomePath(user)} replace />;
  }

  return children;
};

function App() {
  const { user, loading } = useAuth();
  const location = useLocation();
  useNotificationInitializer();
  // Chatbot is a modal (no page); gate its launcher on the 'chatbot' module so a
  // company whose package excludes it never sees the widget. SuperAdmin/company
  // users skip module-access entirely (query disabled) → default to showing it.
  const { data: moduleAccess } = useModuleAccess();
  const chatbotEnabled =
    !!user && (user.isSuperAdmin || user.isCompany || moduleAccess?.modules?.[MODULE_KEYS.CHATBOT] === true);
  const isLeaveVerificationPage = location.pathname.startsWith('/verify/');
  const isUnsubscribePage = location.pathname.startsWith('/unsubscribe');
  const isJobsPage = isJobsSiteOrigin() || location.pathname.startsWith('/jobs');
  const isCareersPage = location.pathname.startsWith('/careers');
  if (loading && !isLeaveVerificationPage && !isUnsubscribePage && !isJobsPage && !isCareersPage) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-background'>
        <div className='h-12 w-12 animate-spin rounded-full border-b-2 border-primary border-t-transparent'></div>
      </div>
    );
  }
  if (isJobsSiteOrigin()) {
    return (
      <>
        <ScrollToTop />
        <JobsSiteRoutes />
        {!user && !isCareersPage && <PublicFooter />}
      </>
    );
  }
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route
          path='/'
          element={
            user ? (
              <Navigate to={getHomePath(user)} replace />
            ) : readCandidateSession() ? (
              <Navigate to={CAREERS_ROUTES.dashboard} replace />
            ) : (
              <LandingPage />
            )
          }
        />
        <Route path='/landing' element={<LandingPage />} />
        <Route
          path='/oauth/authorize'
          element={
            <Suspense fallback={null}>
              <OAuthAuthorizePage />
            </Suspense>
          }
        />
        <Route
          path='/pricing'
          element={
            <Suspense fallback={null}>
              <PricingPage />
            </Suspense>
          }
        />
        <Route
          path='/terms-and-conditions'
          element={
            <Suspense fallback={null}>
              <TermsAndConditionsPage />
            </Suspense>
          }
        />
        <Route
          path='/privacy'
          element={
            <Suspense fallback={null}>
              <PrivacyPage />
            </Suspense>
          }
        />
        <Route
          path='/refund'
          element={
            <Suspense fallback={null}>
              <RefundPage />
            </Suspense>
          }
        />
        <Route
          path='/distance-sales'
          element={
            <Suspense fallback={null}>
              <DistanceSalesPage />
            </Suspense>
          }
        />
        <Route
          path='/about'
          element={
            <Suspense fallback={null}>
              <AboutPage />
            </Suspense>
          }
        />
        <Route
          path={`${MODULE_PAGE_BASE_PATH}/:slug`}
          element={
            <Suspense fallback={null}>
              <ModulePageRoute />
            </Suspense>
          }
        />
        <Route
          path='/verify/:verificationCode'
          element={
            <Suspense fallback={null}>
              <LeaveVerificationPage />
            </Suspense>
          }
        />
        <Route
          path='/unsubscribe'
          element={
            <Suspense fallback={null}>
              <UnsubscribePage />
            </Suspense>
          }
        />

        {/* Candidate Google/LinkedIn sign-in result: a candidate session, not an HR one. */}
        <Route
          path={CANDIDATE_OAUTH_RESULT_PATH}
          element={
            <Suspense fallback={null}>
              <CandidateOAuthPage />
            </Suspense>
          }
        />

        <Route element={<PublicRoutes />}>
          <Route path={LOGIN_PATH} element={<LoginPage />} />
          <Route
            path={SIGNUP_PATH}
            element={
              <Suspense fallback={null}>
                <SignupPage />
              </Suspense>
            }
          />
          <Route path='/reset-password' element={<ResetPasswordPage />} />
          <Route path='/register/invite' element={<CompanyInvitePage />} />
          <Route path='/kvkk-onay' element={<KvkkApprovalPage />} />
          <Route path='/kvkk-approval/:token' element={<KvkkApprovalPage />} />
        </Route>

        <Route element={<PrivateRoutes />}>
          <Route
            path='/connect'
            element={
              <AuthorizedRoute roles={[RoleType.Admin, RoleType.HR]}>
                <ModuleGate moduleKey={MODULE_KEYS.CONNECTIONS}>
                  <CompanyConnectPage />
                </ModuleGate>
              </AuthorizedRoute>
            }
          />
          <Route
            path='/companies'
            element={
              <AuthorizedRoute superAdminOnly>
                <CompaniesPage />
              </AuthorizedRoute>
            }
          />
          <Route
            path='/system'
            element={
              <AuthorizedRoute superAdminOnly>
                <SystemPage />
              </AuthorizedRoute>
            }
          />
          <Route
            path='/package-management'
            element={
              <AuthorizedRoute superAdminOnly>
                <PackageManagementPage />
              </AuthorizedRoute>
            }
          />
          <Route
            path='/invoices'
            element={
              <AuthorizedRoute superAdminOnly>
                <InvoicesPage />
              </AuthorizedRoute>
            }
          />
          <Route
            path='/invoices/:companyId'
            element={
              <AuthorizedRoute superAdminOnly>
                <InvoiceDetailPage />
              </AuthorizedRoute>
            }
          />
          <Route path='/dashboard' element={<DashboardPage />} />
          <Route path='/notifications' element={<NotificationsPage />} />
          <Route
            path='/announcements'
            element={
              <ModuleGate moduleKey={MODULE_KEYS.ANNOUNCEMENTS}>
                <AnnouncementsPage />
              </ModuleGate>
            }
          />
          <Route
            path='/games/sudoku'
            element={
              <ModuleGate moduleKey={MODULE_KEYS.GAMES}>
                <SudokuGamePage />
              </ModuleGate>
            }
          />
          <Route
            path='/employees'
            element={
              <AuthorizedRoute permissions={[Permission.EMPLOYEE_READ_ALL]}>
                <ModuleGate moduleKey={MODULE_KEYS.EMPLOYEES}>
                  <EmployeesPage />
                </ModuleGate>
              </AuthorizedRoute>
            }
          />
          <Route
            path='/employees/:id'
            element={
              <AuthorizedRoute permissions={[Permission.EMPLOYEE_READ_ALL]}>
                <ModuleGate moduleKey={MODULE_KEYS.EMPLOYEES}>
                  <EmployeeDetailPage />
                </ModuleGate>
              </AuthorizedRoute>
            }
          />
          <Route
            path='/employees/:id/offboarding'
            element={
              <AuthorizedRoute permissions={[Permission.EMPLOYEE_OFFBOARDING_MANAGE]}>
                <ModuleGate moduleKey={MODULE_KEYS.EMPLOYEES}>
                  <OffboardingProcessPage />
                </ModuleGate>
              </AuthorizedRoute>
            }
          />
          <Route
            path='/offboarding'
            element={
              <AuthorizedRoute permissions={[Permission.EMPLOYEE_OFFBOARDING_MANAGE]}>
                <ModuleGate moduleKey={MODULE_KEYS.EMPLOYEES}>
                  <OffboardingListPage />
                </ModuleGate>
              </AuthorizedRoute>
            }
          />
          {/* Inventory: one page, two tabs (Envanterlerim / Çalışan Envanterleri), like
              Expenses/Leaves. Any module-entitled employee reaches it; the company-wide
              tab is shown only to read-all/manage users (gated inside the page). */}
          <Route
            path='/inventory'
            element={
              <ModuleGate moduleKey={MODULE_KEYS.INVENTORY}>
                <InventoryPage />
              </ModuleGate>
            }
          />
          {/* Employee self-service: no manage permission — any employee sees only their own process. */}
          <Route
            path='/my-offboarding'
            element={
              <ModuleGate moduleKey={MODULE_KEYS.EMPLOYEES}>
                <MyOffboardingPage />
              </ModuleGate>
            }
          />
          <Route
            path='/groups'
            element={
              <ModuleGate moduleKey={MODULE_KEYS.GROUPS}>
                <GroupsPage />
              </ModuleGate>
            }
          />
          <Route
            path='/groups/:id'
            element={
              <ModuleGate moduleKey={MODULE_KEYS.GROUPS}>
                <GroupDetailPage />
              </ModuleGate>
            }
          />
          <Route path='/c-level-hierarchy' element={<CLevelHierarchyPage />} />
          <Route
            path='/candidates'
            element={
              <AuthorizedRoute permissions={[Permission.CANDIDATE_READ]}>
                <ModuleGate moduleKey={MODULE_KEYS.CANDIDATES}>
                  <CandidatesPage />
                </ModuleGate>
              </AuthorizedRoute>
            }
          />
          <Route
            path='/positions'
            element={
              <AuthorizedRoute permissions={[Permission.POSITION_READ]}>
                <ModuleGate moduleKey={MODULE_KEYS.POSITIONS}>
                  <JobPositionsPage />
                </ModuleGate>
              </AuthorizedRoute>
            }
          />
          <Route
            path='/positions/:id'
            element={
              <AuthorizedRoute permissions={[Permission.POSITION_READ]}>
                <ModuleGate moduleKey={MODULE_KEYS.POSITIONS}>
                  <PositionDetailPage />
                </ModuleGate>
              </AuthorizedRoute>
            }
          />
          <Route
            path='/job-posting'
            element={
              <AuthorizedRoute permissions={[Permission.POSITION_READ]}>
                <ModuleGate moduleKey={MODULE_KEYS.POSITIONS}>
                  <JobPostingPage />
                </ModuleGate>
              </AuthorizedRoute>
            }
          />
          <Route
            path='/calendar'
            element={
              <ModuleGate moduleKey={MODULE_KEYS.CALENDAR}>
                <InterviewsPage />
              </ModuleGate>
            }
          />
          <Route
            path='/exams'
            element={
              <ModuleGate moduleKey={MODULE_KEYS.ASSESSMENTS}>
                <ExamsPage />
              </ModuleGate>
            }
          />
          <Route
            path='/leaves'
            element={
              <ModuleGate moduleKey={MODULE_KEYS.LEAVE}>
                <LeavesPage />
              </ModuleGate>
            }
          />
          <Route
            path='/reports/recruitment'
            element={
              <ModuleGate moduleKey={MODULE_KEYS.REPORTS}>
                <AuthorizedRoute permissions={[Permission.RECRUITMENT_REPORT_READ]}>
                  <ReportsPage />
                </AuthorizedRoute>
              </ModuleGate>
            }
          />
          <Route
            path='/reports/position'
            element={
              <ModuleGate moduleKey={MODULE_KEYS.REPORTS}>
                <AuthorizedRoute permissions={[Permission.RECRUITMENT_REPORT_READ]}>
                  <PositionReportsPage />
                </AuthorizedRoute>
              </ModuleGate>
            }
          />
          <Route
            path='/customer-company'
            element={
              <AuthorizedRoute permissions={[Permission.CUSTOMER_COMPANY_READ]}>
                <ModuleGate moduleKey={MODULE_KEYS.CLIENTS}>
                  <CustomerCompanyPage />
                </ModuleGate>
              </AuthorizedRoute>
            }
          />
          <Route
            path='/applications'
            element={
              <AuthorizedRoute permissions={[Permission.JOB_APPLICATION_READ]}>
                <ModuleGate moduleKey={MODULE_KEYS.JOB_LISTINGS}>
                  <JobApplicationsPage />
                </ModuleGate>
              </AuthorizedRoute>
            }
          />
          <Route path='/profile' element={<ProfilePage />} />
          <Route
            path='/skills-and-titles'
            element={
              <AuthorizedRoute permissions={[Permission.TITLE_READ, Permission.SKILL_READ, Permission.UNIVERSITY_READ]}>
                <ModuleGate moduleKey={MODULE_KEYS.SKILLS_AND_TITLES}>
                  <SkillsAndTitlesPage />
                </ModuleGate>
              </AuthorizedRoute>
            }
          />
          <Route
            path='/candidate-matching'
            element={
              <AuthorizedRoute permissions={[Permission.POSITION_READ]}>
                {/* Gated by the page: candidate matching, or the job-listings module for the pool alone. */}
                <CandidateMatchingPage />
              </AuthorizedRoute>
            }
          />
          <Route
            path='/recruitment-pipeline/:positionId'
            element={
              <AuthorizedRoute permissions={[Permission.POSITION_READ]}>
                <ModuleGate moduleKey={MODULE_KEYS.POSITIONS}>
                  <RecruitmentPipelinePage />
                </ModuleGate>
              </AuthorizedRoute>
            }
          />
          <Route
            path='/candidates-profile/:id'
            element={
              <AuthorizedRoute permissions={[Permission.CANDIDATE_READ]}>
                <ModuleGate moduleKey={MODULE_KEYS.CANDIDATES}>
                  <CandidateProfilePage />
                </ModuleGate>
              </AuthorizedRoute>
            }
          />
          <Route
            path='/expenses'
            element={
              <ModuleGate moduleKey={MODULE_KEYS.EXPENSES}>
                <ExpensesManagementPage />
              </ModuleGate>
            }
          />
          <Route
            path='/working-hours'
            element={
              <ModuleGate moduleKey={MODULE_KEYS.OVERTIME}>
                <OvertimePage />
              </ModuleGate>
            }
          />
          <Route
            path='/settings'
            element={
              <AuthorizedRoute
                predicate={({ user, hasPermission, ability }) =>
                  Boolean(
                    user &&
                    (user.role?.name === RoleType.Admin ||
                      hasPermission(Permission.ACCOUNT_SETTINGS_MANAGE) ||
                      hasPermission(Permission.LEAVE_POLICY_MANAGE) ||
                      ability.can('manage', 'PolicyDocument'))
                  )
                }
              >
                <SettingsRoute>
                  <SettingsPage />
                </SettingsRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path='/account-settings'
            element={
              <AuthorizedRoute permissions={[Permission.ACCOUNT_SETTINGS_MANAGE]}>
                <AccountSettingsPage />
              </AuthorizedRoute>
            }
          />
          <Route
            path={LISTING_TEAM_PATH}
            element={
              <AuthorizedRoute
                permissions={[Permission.ACCOUNT_SETTINGS_MANAGE]}
                predicate={({ user }) => isListingOnlyUser(user)}
              >
                <ListingTeamPage />
              </AuthorizedRoute>
            }
          />
          <Route
            path='/performance-360/evaluations'
            element={
              <ModuleGate moduleKey={MODULE_KEYS.EVALUATIONS}>
                <Performance360EvaluationsPage />
              </ModuleGate>
            }
          />
          <Route
            path='/performance-360/forms'
            element={
              <AuthorizedRoute permissions={[Permission.FORM360_TEMPLATE_READ]}>
                <ModuleGate moduleKey={MODULE_KEYS.EVALUATIONS}>
                  <Performance360FormsPage />
                </ModuleGate>
              </AuthorizedRoute>
            }
          />

          <Route
            path='/performance-360/forms/new'
            element={
              <AuthorizedRoute permissions={[Permission.FORM360_TEMPLATE_CREATE]}>
                <ModuleGate moduleKey={MODULE_KEYS.EVALUATIONS}>
                  <Performance360FormCreatePage />
                </ModuleGate>
              </AuthorizedRoute>
            }
          />

          <Route
            path='/performance-360/goals'
            element={
              <ModuleGate moduleKey={MODULE_KEYS.GOALS}>
                <Performance360GoalsPage />
              </ModuleGate>
            }
          />
          <Route path='/profile/360-results' element={<Performance360MyResultsPage />} />
        </Route>

        {/* ── Peoply Kariyer (candidate area) — guarded by candidate session, not HR auth ── */}
        <Route element={<CareersRoutes />}>{careersAreaRoutes}</Route>

        {/* Public job listings — no login needed */}
        <Route
          path={PUBLIC_JOB_LISTINGS_PATH}
          element={
            <Suspense fallback={null}>
              <PublicJobsPage />
            </Suspense>
          }
        />
        <Route
          path={PUBLIC_COMPANY_ROUTE}
          element={
            <Suspense fallback={null}>
              <PublicCompanyPage />
            </Suspense>
          }
        />
        <Route
          path={PUBLIC_JOB_ROUTE}
          element={
            <Suspense fallback={null}>
              <PublicJobPage />
            </Suspense>
          }
        />

        <Route element={<CompanyRoutes />}>
          <Route path='/company-dashboard' element={<CompanyDashboardPage />} />
          <Route path='/company-positions' element={<CompanyJobPositionsPage />} />
          <Route path='/company-position-detail/:id' element={<CompanyJobPositionDetailPage />} />
          <Route path='/company-connect' element={<CompanyConnectPage />} />
          <Route path='/company-notifications' element={<NotificationsPage />} />
        </Route>

        <Route
          path='*'
          element={
            <Suspense fallback={null}>
              <NotFoundPage />
            </Suspense>
          }
        />
      </Routes>

      {user && !user.isSuperAdmin && !user.isCompany && chatbotEnabled && (
        <Suspense fallback={null}>
          <ChatDialog />
        </Suspense>
      )}

      {!user && !isCareersPage && <PublicFooter />}
    </>
  );
}
export default App;
